/**
 * MyAccount — self-service account page
 *
 * Available to EVERY authenticated tenant user (not module-gated) so a user
 * provisioned by an admin can maintain their own record. Three tabs
 * (?tab=profile|security|organization — deep-linkable):
 *   • Profile        → photo (POST/DELETE /auth/me/avatar) + details (PATCH /auth/me)
 *   • Security       → password (POST /auth/change-password, or /auth/set-password
 *                      for a Google-only account — see `hasPassword` on GET /auth/me)
 *                      + signed-in devices (GET/POST /auth/sessions…, ActiveSessions)
 *   • Organization   → name (PATCH /auth/organization) + logo
 *                      (POST/DELETE /auth/organization/logo) — tenant_owner only
 *
 * Both images are upload-only (no pasted-URL inputs): a pasted URL used to silently
 * lose to an uploaded one, so that path was retired rather than fixed in place.
 * Sections are read-first with an explicit Edit → Save / Cancel (see AccountCard).
 *
 * One GET /auth/me on mount (fresh, not just Redux, so branding/hasPassword are
 * present). Saves push their result straight into the store — the sidebar's name and
 * photo update with no extra round trip.
 */

import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Snackbar,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { Building2, Mail, ShieldCheck, UserRound } from 'lucide-react';
import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useSearchParams } from 'react-router-dom';

import { ACCENT } from '@/components/account/AccountCard';
import PageHeader from '@/components/ui/PageHeader';
import { useAuth } from '@/hooks/useAuth';
import apiService from '@/services/api.service';
import { updateTenant, updateUser } from '@/store/slices/authSlice';
import { roleLabel } from '@/utils/roleLabel.util';

import OrganizationPanel from './account/OrganizationPanel';
import ProfilePanel, { ProfileData } from './account/ProfilePanel';
import SecurityPanel from './account/SecurityPanel';
import { extractError, Notify } from './account/shared';

type Snack = { open: boolean; message: string; severity: 'success' | 'error' };
type TabKey = 'profile' | 'security' | 'organization';

const EMPTY_PROFILE: ProfileData = {
  firstName: '',
  lastName: '',
  email: '',
  phoneNumber: '',
  avatar: '',
};

const MyAccount: React.FC = () => {
  const { user, tenant } = useAuth();
  const dispatch = useDispatch();
  const [params, setParams] = useSearchParams();
  const isOwner = user?.role === 'tenant_owner';

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [snack, setSnack] = useState<Snack>({ open: false, message: '', severity: 'success' });
  const [profile, setProfile] = useState<ProfileData>(EMPTY_PROFILE);
  const [hasPassword, setHasPassword] = useState(true);
  const [orgName, setOrgName] = useState('');
  const [logoUrl, setLogoUrl] = useState('');

  const notify: Notify = useCallback(
    (message, severity) => setSnack({ open: true, message, severity }),
    []
  );

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError('');
    try {
      const res = await apiService.getCurrentUser();
      const u = res?.data?.user || {};
      const t = res?.data?.tenant || {};
      setProfile({
        firstName: u.firstName || '',
        lastName: u.lastName || '',
        email: u.email || '',
        phoneNumber: u.phoneNumber || '',
        avatar: u.avatar || '',
      });
      setHasPassword(u.hasPassword !== false);
      setOrgName(t.name || '');
      setLogoUrl(t.branding?.logoUrl || '');
    } catch (error: any) {
      setLoadError(extractError(error, 'Failed to load your account'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  // Results go straight into the store: no /auth/me round trip to refresh the sidebar.
  const syncUser = (patch: Partial<NonNullable<typeof user>>) => {
    if (user) dispatch(updateUser({ user: { ...user, ...patch } }));
  };

  const tab: TabKey = (() => {
    const wanted = params.get('tab');
    if (wanted === 'security') return 'security';
    if (wanted === 'organization' && isOwner) return 'organization';
    return 'profile';
  })();
  const setTab = (next: TabKey) =>
    setParams(next === 'profile' ? {} : { tab: next }, { replace: true });

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (loadError) {
    return (
      <Box sx={{ maxWidth: 560, mx: 'auto', py: 6 }}>
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={() => void load()}>
              Retry
            </Button>
          }
        >
          {loadError}
        </Alert>
      </Box>
    );
  }

  const initials =
    `${profile.firstName.charAt(0)}${profile.lastName.charAt(0)}`.toUpperCase() || '?';

  return (
    <Box sx={{ maxWidth: 1120, mx: 'auto' }}>
      <PageHeader
        title="My Account"
        subtitle={`Manage your personal details, password${isOwner ? ', and organization' : ''}.`}
      />

      {/* ── Identity strip ──────────────────────────────────────────────── */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2.5,
          flexWrap: 'wrap',
          p: { xs: 2, sm: 3 },
          mb: 3,
          bgcolor: 'background.paper',
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: '12px',
        }}
      >
        <Box
          sx={{
            width: 64,
            height: 64,
            borderRadius: '50%',
            flexShrink: 0,
            overflow: 'hidden',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: alpha(ACCENT, 0.08),
            color: ACCENT,
            fontSize: '1.375rem',
            fontWeight: 600,
            border: '1px solid',
            borderColor: 'divider',
          }}
        >
          {profile.avatar ? (
            <Box
              component="img"
              src={profile.avatar}
              alt=""
              sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
            />
          ) : (
            initials
          )}
        </Box>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <Typography sx={{ fontSize: '1.25rem', fontWeight: 700, color: 'text.primary' }} noWrap>
              {profile.firstName} {profile.lastName}
            </Typography>
            {user?.role && (
              <Chip
                label={roleLabel(user.role)}
                size="small"
                sx={{ bgcolor: alpha(ACCENT, 0.1), color: '#4338ca', fontWeight: 600 }}
              />
            )}
          </Box>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              flexWrap: 'wrap',
              mt: 0.5,
              color: 'text.secondary',
              fontSize: '0.8125rem',
            }}
          >
            <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
              <Mail size={14} strokeWidth={1.75} />
              <span>{profile.email}</span>
            </Box>
            {orgName && (
              <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
                <Building2 size={14} strokeWidth={1.75} />
                <span>{orgName}</span>
              </Box>
            )}
          </Box>
        </Box>
      </Box>

      {/* ── Tabs ────────────────────────────────────────────────────────── */}
      <Tabs
        value={tab}
        onChange={(_, v: TabKey) => setTab(v)}
        sx={{ mb: 3, borderBottom: '1px solid', borderColor: 'divider' }}
      >
        <Tab
          value="profile"
          label="Profile"
          icon={<UserRound size={16} strokeWidth={1.75} />}
          iconPosition="start"
        />
        <Tab
          value="security"
          label="Security"
          icon={<ShieldCheck size={16} strokeWidth={1.75} />}
          iconPosition="start"
        />
        {isOwner && (
          <Tab
            value="organization"
            label="Organization"
            icon={<Building2 size={16} strokeWidth={1.75} />}
            iconPosition="start"
          />
        )}
      </Tabs>

      {tab === 'profile' && (
        <ProfilePanel
          profile={profile}
          notify={notify}
          onSaved={(patch) => {
            setProfile((p) => ({ ...p, ...patch }));
            syncUser(patch);
          }}
          onAvatarChange={(url) => {
            setProfile((p) => ({ ...p, avatar: url }));
            syncUser({ avatar: url });
          }}
        />
      )}
      {tab === 'security' && (
        <SecurityPanel
          hasPassword={hasPassword}
          onPasswordSet={() => setHasPassword(true)}
          notify={notify}
        />
      )}
      {tab === 'organization' && isOwner && (
        <OrganizationPanel
          orgName={orgName}
          logoUrl={logoUrl}
          notify={notify}
          onNameSaved={(name) => {
            setOrgName(name);
            if (tenant) dispatch(updateTenant({ tenant: { ...tenant, name } }));
          }}
          onLogoChange={setLogoUrl}
        />
      )}

      <Snackbar
        open={snack.open}
        autoHideDuration={5000}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={snack.severity}
          variant="outlined"
          onClose={() => setSnack((s) => ({ ...s, open: false }))}
          sx={{
            borderRadius: 2.5,
            fontWeight: 500,
            boxShadow: '0 8px 24px rgba(15,23,42,0.12)',
            ...(snack.severity === 'success'
              ? { bgcolor: '#ecfdf5', color: '#065f46', borderColor: '#a7f3d0', '& .MuiAlert-icon': { color: '#059669' } }
              : { bgcolor: '#fef2f2', color: '#991b1b', borderColor: '#fecaca', '& .MuiAlert-icon': { color: '#dc2626' } }),
          }}
        >
          {snack.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default MyAccount;
